import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PROSPECT_ACTIVITY_LABELS } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Loader2, Phone, Rocket, Gauge, Flame, AlertTriangle, User, CalendarRange, TrendingUp, UserPlus, CalendarClock, ChevronRight, ArrowRight, ListChecks } from "lucide-react";

const FUNNEL_ORDER = ["Da contattare", "Contattato", "In sviluppo", "Interessato", "Convertito in Lead"];
const TODAY_CFG = {
  da_contattare: { icon: UserPlus, tone: "text-primary" },
  followup: { icon: CalendarClock, tone: "text-blue-500" },
  scadute: { icon: AlertTriangle, tone: "text-red-500" },
  interessati: { icon: Flame, tone: "text-orange-500" },
};

export default function ProspectDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["admin", "team_leader"].includes(user?.role);
  const [d, setD] = useState(null);
  const [hist, setHist] = useState(null);
  const [users, setUsers] = useState([]);
  const [today, setToday] = useState(null);
  const [actModal, setActModal] = useState(null);
  const [uid, setUid] = useState("");
  useEffect(() => {
    const qs = uid ? `?user_id=${encodeURIComponent(uid)}` : "";
    api.get(`/prospects/dashboard${qs}`).then((r) => setD(r.data)).catch(() => setD(null));
    api.get(`/prospects/dashboard/weekly-history?weeks=8${uid ? `&user_id=${encodeURIComponent(uid)}` : ""}`).then((r) => setHist(r.data)).catch(() => {});
    api.get(`/prospects/dashboard/today-actions${qs}`).then((r) => setToday(r.data)).catch(() => {});
  }, [uid]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  const usersMap = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u.name])), [users]);
  if (!d) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const funnelMax = Math.max(1, ...FUNNEL_ORDER.map((s) => d.by_status?.[s] || 0));

  return (
    <div data-testid="prospect-dashboard-page">
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div><h1 className="text-xl font-heading font-bold">Dashboard Prospect</h1><p className="text-xs text-muted-foreground">{uid ? "Vista del singolo commerciale" : "Attività di sviluppo commerciale"}</p></div>
        {isAdmin && (
          <Select value={uid || "all"} onValueChange={(v) => setUid(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="pd-user-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutto il team</SelectItem>
              {(users || []).filter((u) => ["sales", "qualification", "partner", "team_leader"].includes(u.role)).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </header>

      <div className="p-4 lg:p-8 space-y-6">
        {today && today.blocks && (
          <Card className="rounded border-border p-4 lg:p-5" data-testid="pd-today">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-4"><ListChecks className="h-3.5 w-3.5" /> Le mie azioni di oggi</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {today.blocks.map((b) => {
                const cfg = TODAY_CFG[b.key] || {};
                const Icon = cfg.icon || AlertTriangle;
                const active = b.count > 0;
                return (
                  <button key={b.key} disabled={!active} onClick={() => active && setActModal(b)} data-testid={`pd-today-${b.key}`}
                    className={`text-left rounded-md border p-3 transition-colors ${active ? "border-border hover:border-primary/50 cursor-pointer" : "border-border/50 opacity-60 cursor-default"}`}>
                    <div className="flex items-center justify-between">
                      <Icon className={`h-4 w-4 ${active ? cfg.tone : "text-muted-foreground"}`} />
                      {active && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                    </div>
                    <div className={`font-heading font-bold text-2xl mt-2 ${active ? cfg.tone : "text-muted-foreground"}`}>{b.count}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{b.label}</div>
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        <Card className="rounded border-border p-4 lg:p-5" data-testid="pd-weekly">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-4"><CalendarRange className="h-3.5 w-3.5" /> Questa settimana · obiettivi</div>
          <div className="space-y-3.5">
            {(d.weekly || []).map((r, i) => (
              <div key={r.label} data-testid={`pd-weekly-row-${i}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium truncate pr-2">{r.label}</span>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-muted-foreground text-xs">Target <b className="text-foreground">{r.target}</b></span>
                    <span className="text-xs">Fatto <b className={r.pct >= 100 ? "text-emerald-500" : "text-foreground"} data-testid={`pd-weekly-done-${i}`}>{r.done}</b></span>
                    <span className={`text-xs font-bold w-10 text-right ${r.pct >= 100 ? "text-emerald-500" : "text-muted-foreground"}`}>{r.pct}%</span>
                  </div>
                </div>
                <div className="h-2 rounded bg-secondary/40 overflow-hidden">
                  <div className={`h-full rounded transition-all ${r.pct >= 100 ? "bg-emerald-500" : "bg-primary/70"}`} style={{ width: `${r.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {hist && hist.series && (
          <Card className="rounded border-border p-4 lg:p-5" data-testid="pd-weekly-history">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-4"><CalendarRange className="h-3.5 w-3.5" /> Storico settimane · andamento obiettivi (ultime {hist.weeks})</div>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {hist.series.map((s) => {
                const max = Math.max(s.target_ref, ...s.values, 1);
                const last = s.values[s.values.length - 1] ?? 0;
                return (
                  <div key={s.label} className="rounded-md border border-border p-3" data-testid={`pd-hist-${s.label}`}>
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-xs font-medium truncate pr-1">{s.label}</span>
                      <span className={`text-sm font-heading font-bold ${last >= s.target_ref ? "text-emerald-500" : ""}`}>{last}</span>
                    </div>
                    <div className="flex items-end gap-1 h-12">
                      {s.values.map((v, i) => (
                        <div key={i} className="flex-1 flex flex-col justify-end" title={`${hist.labels[i]}: ${v}`}>
                          <div className={`w-full rounded-sm ${v >= s.target_ref ? "bg-emerald-500" : "bg-primary/60"} ${i === s.values.length - 1 ? "opacity-100" : "opacity-80"}`} style={{ height: `${Math.max(3, Math.round((v / max) * 46))}px` }} />
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between mt-1 text-[9px] text-muted-foreground">
                      <span>{hist.labels[0]}</span><span>{hist.labels[hist.labels.length - 1]}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <CoperturaCard cov={d.copertura} />
          <Kpi icon={TrendingUp} label="Prospect avanzati (sett.)" value={d.avanzati_week ?? 0} tone="text-emerald-500" />
          <Kpi icon={Rocket} label="Convertiti in Lead" value={d.convertiti} tone="text-emerald-500" />
          <Kpi icon={Gauge} label="Tasso conversione" value={`${d.tasso_conversione}%`} />
          <Kpi icon={Flame} label="Interessati" value={d.interessati} tone="text-orange-500" />
          <Kpi icon={Phone} label="Prossime attività" value={d.prossime_attivita} />
          <Kpi icon={AlertTriangle} label="Attività scadute" value={d.attivita_scadute ?? 0} tone="text-red-500" />
          <Kpi icon={Gauge} label="Qualità media note" value={d.quality_media != null ? `${d.quality_media}/100` : "—"} />
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="rounded border-border p-4 lg:col-span-2" data-testid="pd-funnel">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Funnel di conversione</div>
            <div className="space-y-2">
              {FUNNEL_ORDER.map((s) => {
                const v = d.by_status?.[s] || 0;
                return (
                  <div key={s} className="flex items-center gap-3">
                    <div className="w-32 text-xs text-muted-foreground shrink-0 truncate">{s}</div>
                    <div className="flex-1 h-6 rounded bg-secondary/40 overflow-hidden">
                      <div className="h-full bg-primary/70 rounded flex items-center justify-end px-2 transition-all" style={{ width: `${Math.max(4, (v / funnelMax) * 100)}%` }}>
                        <span className="text-[11px] font-bold text-primary-foreground">{v}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="rounded border-border p-4" data-testid="pd-hot">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><Flame className="h-3.5 w-3.5 text-orange-500" /> Prospect caldi</div>
            {(!d.hot || d.hot.length === 0) ? <p className="text-sm text-muted-foreground">Nessun prospect caldo.</p> : (
              <div className="space-y-2">
                {d.hot.map((p) => (
                  <button key={p.id} onClick={() => navigate(`/prospects/${p.id}`)} className="w-full text-left rounded-md border border-border p-2 hover:border-primary/50 transition-colors" data-testid={`pd-hot-${p.id}`}>
                    <div className="text-sm font-semibold truncate">{p.company_name}</div>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground flex-wrap">
                      <span className="px-1.5 py-0.5 rounded bg-secondary">{p.status}</span>
                      {(p.city || p.province) && <span>{[p.city, p.province].filter(Boolean).join(" · ")}</span>}
                      {p.assigned_user_id && usersMap[p.assigned_user_id] && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{usersMap[p.assigned_user_id]}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Breakdown title="Per categoria" data={d.by_category} />
          <Breakdown title="Per stato" data={d.by_status} />
          <Breakdown title="Attività per tipo" data={Object.fromEntries(Object.entries(d.activities).map(([k, v]) => [PROSPECT_ACTIVITY_LABELS[k] || k, v]))} />
        </div>
        <Breakdown title="Attività per commerciale" data={d.by_user} />
      </div>

      <Dialog open={!!actModal} onOpenChange={(o) => !o && setActModal(null)}>
        <DialogContent data-testid="pd-today-dialog">
          <DialogHeader><DialogTitle className="text-base">{actModal?.label} {actModal ? `(${actModal.count})` : ""}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-2">
            {(actModal?.items || []).length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">Nessun prospect.</p>
            ) : actModal.items.map((p) => (
              <button key={p.id} onClick={() => { setActModal(null); navigate(`/prospects/${p.id}`); }} data-testid={`pd-today-item-${p.id}`}
                className="w-full text-left px-3 py-2.5 rounded hover:bg-secondary flex items-center justify-between gap-2 border-b border-border/50 last:border-0">
                <span className="min-w-0">
                  <span className="text-sm font-medium">{p.name}</span>
                  {(p.city || p.province) && <span className="text-xs text-muted-foreground ml-2">{[p.city, p.province].filter(Boolean).join(" · ")}</span>}
                  {p.status && <span className="text-[10px] text-muted-foreground ml-2 px-1.5 py-0.5 rounded bg-secondary">{p.status}</span>}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CoperturaCard({ cov }) {
  if (!cov) return <Kpi icon={Gauge} label="Copertura Prospect" value="—" />;
  const Row = ({ label, pct, done, tot }) => (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-heading font-semibold">{pct}% <span className="text-muted-foreground font-normal">({done}/{tot})</span></span>
      </div>
      <div className="h-1.5 rounded bg-secondary/40 overflow-hidden"><div className="h-full rounded bg-primary/70 transition-all" style={{ width: `${Math.min(100, pct)}%` }} /></div>
    </div>
  );
  return (
    <Card className="rounded border-border p-4" data-testid="pd-kpi-copertura">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-2"><Gauge className="h-3.5 w-3.5" /> Copertura Prospect</div>
      <div className="space-y-2.5">
        <Row label="Aziende" pct={cov.aziende_pct} done={cov.aziende_contattati} tot={cov.aziende_total} />
        <Row label="Studi tecnici" pct={cov.studi_pct} done={cov.studi_contattati} tot={cov.studi_total} />
      </div>
    </Card>
  );
}
function Kpi({ icon: Icon, label, value, tone }) {
  return <Card className="rounded border-border p-4" data-testid={`pd-kpi-${label}`}><div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-1"><Icon className="h-3.5 w-3.5" /> {label}</div><div className={`font-heading font-bold text-2xl ${tone || ""}`}>{value}</div></Card>;
}
function Breakdown({ title, data }) {
  const entries = Object.entries(data || {}).filter(([, v]) => v > 0);
  return (
    <Card className="rounded border-border p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">{title}</div>
      {entries.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : (
        <div className="space-y-1.5">{entries.map(([k, v]) => <div key={k} className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{k}</span><span className="font-heading font-semibold">{v}</span></div>)}</div>
      )}
    </Card>
  );
}
